import type { UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs, TabsList, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { inputTitle, useReportInputs } from "@/profitability/missing-value";
import type { ReportColumn } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import { fileTitle, isNoMatch } from "./figure-words";
import { SourceDocumentHeader } from "./source-document-header";
import { SourceEmpty } from "./source-empty";
import { SourceFile } from "./source-file";
import { figureFiles, figureParts, inFile } from "./sourced";
import type { CitationsAnswer, FilePart } from "./types";

/** Why a file opens with no row: nothing matched, or not traced to one. */
const unplacedWords = (answer: CitationsAnswer) => {
  const { figure } = answer;
  if (isNoMatch(figure))
    return `${figure?.note ?? "Nothing in this file matched"}. This is the file it would come from.`;
  if (figure?.how === "formula" || figure?.how === "share")
    return "This figure is worked out from others, so no row holds it. This is the file its column names.";
  return "This report doesn't trace this figure to a row of the file yet. This is the file it comes from.";
};

/**
 * The files one figure (or the part being looked at) came from: a tab per
 * file, its name and the place being looked at above it, and the file open
 * with all the figure's rows in it highlighted. A figure no row holds opens
 * the file its column names at the top, saying why, or explains itself (a
 * formula, a setting).
 */
export const SourceDocument = ({
  businessId,
  reportId,
  runId,
  compare,
  column,
  answer,
  partIndex,
  settingsHref,
  onLeave,
}: {
  businessId: string;
  reportId: string;
  runId: string;
  compare: string | null;
  /** The figure's column; null for the record as a whole. */
  column: ReportColumn | null;
  answer: UseQueryResult<CitationsAnswer>;
  /** The part being looked at; null for the figure as a whole. */
  partIndex: number | null;
  settingsHref: string;
  /** Called as a link leaves the dialog. */
  onLeave: () => void;
}) => {
  const inputs = useReportInputs();
  const parts = figureParts(answer.data);
  const files = figureFiles(parts);
  const chosen = partIndex === null ? null : (parts[partIndex] ?? null);
  const placed = parts.filter(inFile);
  const lead =
    placed.find((part) => part === chosen) ??
    placed.find((part) => part.used) ??
    placed[0];
  // A tab picked by hand holds until another figure or part is chosen.
  const looking = `${column?.key ?? ""}:${partIndex ?? ""}`;
  const [picked, setPicked] = useState<{ for: string; doc: string } | null>(
    null,
  );
  const doc =
    (picked?.for === looking ? picked.doc : null) ??
    lead?.document_id ??
    files[0]?.document_id ??
    null;

  if (answer.isPending)
    return <Skeleton className="m-4 min-h-0 flex-1 rounded-xl" />;
  if (answer.isError)
    return (
      <ErrorState
        illustration="compact"
        title="Where this figure came from could not be loaded"
        description={reportError(answer.error)}
        retrying={answer.isFetching}
      >
        <Button onClick={() => void answer.refetch()}>Try again</Button>
      </ErrorState>
    );

  const label = column ? (column.label ?? column.key) : "The record";
  const inDoc = placed.filter((part: FilePart) => part.document_id === doc);
  const selected =
    inDoc.find((part) => part === chosen) ??
    inDoc.find((part) => part.used) ??
    inDoc[0] ??
    null;
  // No row of a file: the file the column names, opened at its top.
  const opening = files.length ? null : (answer.data.citations[0] ?? null);
  const shown = selected ?? opening;
  if (!shown)
    return (
      <SourceEmpty
        label={label}
        figure={answer.data.figure}
        input={inputTitle(inputs, column?.source)}
        settingsHref={settingsHref}
        onLeave={onLeave}
      />
    );

  return (
    <Stack gap={3} align="stretch" className="min-h-0 flex-1 p-4">
      {files.length > 0 && (
        <Tabs
          value={doc}
          onValueChange={(value: string) =>
            setPicked({ for: looking, doc: value })
          }
        >
          <TabsList
            variant="line"
            aria-label={`Files ${label} came from`}
            className="max-w-full justify-start overflow-x-auto"
          >
            {files.map((file) => (
              <TabsTrigger
                key={file.document_id}
                value={file.document_id}
                className="flex-none"
              >
                {fileTitle(file)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      <SourceDocumentHeader
        businessId={businessId}
        reportId={reportId}
        runId={runId}
        compare={compare}
        citation={shown}
        onLeave={onLeave}
      />
      {!selected && (
        <Alert
          color={isNoMatch(answer.data.figure) ? "warning" : "information"}
          role="status"
        >
          <AlertDescription>{unplacedWords(answer.data)}</AlertDescription>
        </Alert>
      )}
      <SourceFile
        // Each file its own viewer, so switching tabs opens it afresh.
        key={shown.document_id}
        businessId={businessId}
        file={shown}
        parts={inDoc}
        selected={selected}
      />
    </Stack>
  );
};
